import React from 'react';
import moment from 'moment-timezone';

import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * AppointmentPicker
 *
 * Day, then time. Only days with an open slot are offered at all — a month
 * grid of mostly greyed-out dates is a page of reasons not to book, and two
 * weeks of real options fits on a phone without paging.
 *
 * The first day is picked for them so the times are showing on arrival. The
 * time is always their choice.
 */
const AppointmentPicker = ({ days, onSelect, selected, timezone }) => {

    const mobile = Utils.isMobile();
    const activeDate = selected?.date || days[0]?.date;
    const activeDay = days.find(day => day.date === activeDate) || days[0];

    // only worth saying when the visitor is somewhere else - otherwise it is
    // a line of noise under every appointment
    const showZone = timezone && moment.tz(timezone).utcOffset() !== moment().utcOffset();

    const getOption = ({ active, children, key, onClick }) => (
        <button
        aria-pressed={active}
        className={'pressable'}
        key={key}
        onClick={onClick}
        type={'button'}
        style={{
            alignItems: 'center',
            backgroundColor: active ? Theme.colors.primary() : Theme.colors.white,
            border: `1.5px solid ${active ? Theme.colors.primary() : Theme.colors.border()}`,
            borderRadius: 14,
            color: active ? Theme.colors.white : Theme.colors.text(),
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'inherit',
            justifyContent: 'center',
            padding: '12px 8px',
            transition: 'background-color 0.15s ease, border-color 0.15s ease'
        }}>
            {children}
        </button>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 }}>
            <div>
                <span style={{ ...Theme.textStyles.label(), display: 'block', marginBottom: 10 }}>{'Day'}</span>
                <div style={{
                    display: 'grid',
                    gap: 8,
                    gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 72 : 84}px, 1fr))`
                }}>
                    {days.map(day => {
                        let date = moment(day.date, 'YYYY-MM-DD');
                        let active = day.date === activeDay?.date;
                        return getOption({
                            active,
                            key: day.date,
                            onClick: () => onSelect(active ? selected : { date: day.date, time: null }),
                            children: (
                                <>
                                    <span style={{ fontSize: 12, fontWeight: 500, opacity: active ? 0.85 : 0.6 }}>{date.format('ddd')}</span>
                                    <span style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2 }}>{date.format('D')}</span>
                                    <span style={{ fontSize: 12, fontWeight: 500, opacity: active ? 0.85 : 0.6 }}>{date.format('MMM')}</span>
                                </>
                            )
                        });
                    })}
                </div>
            </div>

            {activeDay && (
                <div>
                    <span style={{ ...Theme.textStyles.label(), display: 'block', marginBottom: 10 }}>
                        {`Time on ${moment(activeDay.date, 'YYYY-MM-DD').format('dddd, MMMM D')}`}
                    </span>
                    <div style={{
                        display: 'grid',
                        gap: 8,
                        gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 96 : 110}px, 1fr))`
                    }}>
                        {activeDay.times.map(time => getOption({
                            active: selected?.date === activeDay.date && selected?.time === time,
                            key: time,
                            onClick: () => onSelect({ date: activeDay.date, time: time }),
                            children: (
                                <span style={{ fontSize: 15, fontWeight: 600 }}>
                                    {moment(time, 'HH:mm:ss').format('h:mm A')}
                                </span>
                            )
                        }))}
                    </div>
                    {showZone && (
                        <div style={{ ...Theme.textStyles.caption({ color: Theme.colors.subText() }), marginTop: 10 }}>
                            {`Times are local to the dealership (${moment.tz(timezone).format('z')}).`}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default AppointmentPicker;
